// <delivery-picker>: pickup or delivery, day, time slot, postcode and notes, saved as cart attributes
// (names in docs/specs/data-contract.md). Checkout buttons in the same bag stay blocked until it's complete.

import { buildCalendar } from '@theme/dates';
import { checkPostcode, parseZones } from '@theme/validate';
import { updateAttributes } from '@theme/cart-api';
import { loadBooked, dateChips, slotChips } from '@theme/calendar-ui';
import { $, $$, debounce, formatPrice } from '@theme/utils';

const config = () => window.theme?.delivery || {};

class DeliveryPicker extends HTMLElement {
  async connectedCallback() {
    const saved = JSON.parse($('script[data-cart-attributes]', this)?.textContent || '{}');
    this.state = {
      method: saved.Fulfilment || '',
      date: saved.Date || '',
      slot: saved.Time || '',
      postcode: saved.Postcode || '',
      notes: saved['Delivery notes'] || '',
      surprise: Boolean(saved.Surprise),
    };
    this.zones = parseZones(config().zones);
    this.save = debounce(() => this.persist(), 500);

    this.restoreInputs();
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('click', this.onClick);
    this.guardCheckout();

    this.days = buildCalendar(config());
    this.renderDates();
    this.renderZone();
    this.update();
    const booked = await loadBooked(this.days);
    this.days = buildCalendar(config(), booked);
    this.renderDates();
  }

  restoreInputs() {
    const method = $(`input[name="dp-method-${this.id}"][value="${this.state.method}"]`, this);
    if (method) method.checked = true;
    $('[data-postcode]', this).value = this.state.postcode;
    $('[data-notes]', this).value = this.state.notes;
    $('[data-surprise]', this).checked = this.state.surprise;
  }

  onChange = (event) => {
    if (event.target.matches('[data-method]')) {
      this.state.method = event.target.value;
      this.save();
      this.update();
    }
    if (event.target.matches('[data-surprise]')) {
      this.state.surprise = event.target.checked;
      this.save();
    }
  };

  onInput = (event) => {
    if (event.target.matches('[data-postcode]')) {
      this.state.postcode = checkPostcode(event.target.value).value.slice(0, 4);
      event.target.value = this.state.postcode;
      this.renderZone();
      this.save();
      this.update();
    }
    if (event.target.matches('[data-notes]')) {
      this.state.notes = event.target.value.slice(0, 300);
      this.save();
    }
  };

  onClick = (event) => {
    const date = event.target.closest('[data-date]');
    const slot = event.target.closest('[data-slot]');
    if (date && !date.disabled) {
      this.state.date = date.dataset.date;
      const day = this.days.find((d) => d.iso === this.state.date);
      if (!day?.slots.find((s) => s.name === this.state.slot && !s.disabled)) this.state.slot = '';
      this.renderDates();
      this.save();
      this.update();
    }
    if (slot && !slot.disabled) {
      this.state.slot = slot.dataset.slot;
      this.renderSlots();
      this.save();
      this.update();
    }
  };

  get zone() {
    return this.zones.find((z) => z.postcode === this.state.postcode);
  }

  /** What's still needed before checkout, in plain words. Empty when ready. */
  get missing() {
    const s = this.state;
    const needs = [];
    if (!s.method) needs.push('pickup or delivery');
    if (!s.date) needs.push('a day');
    else if (!s.slot) needs.push('a time');
    if (s.method === 'Delivery' && !this.zone) needs.push(s.postcode.length === 4 ? 'a postcode we deliver to' : 'your delivery postcode');
    return needs;
  }

  renderDates() {
    const holder = $('[data-dates]', this);
    if (this.state.date && !this.days.find((d) => d.iso === this.state.date && !d.disabled)) {
      this.state.date = '';
      this.state.slot = '';
    }
    holder.innerHTML = dateChips(this.days, this.state.date);
    this.renderSlots();
  }

  renderSlots() {
    $('[data-slots]', this).innerHTML = slotChips(this.days.find((d) => d.iso === this.state.date), this.state.slot);
  }

  renderZone() {
    const out = $('[data-zone]', this);
    const { postcode } = this.state;
    if (postcode.length < 4) {
      out.className = 'hint';
      out.textContent = 'We deliver around Aintree, Melton, Caroline Springs and Taylors Hill.';
      return;
    }
    if (this.zone) {
      out.className = 'notice notice--ok';
      out.textContent = `We deliver to ${this.zone.suburbs || postcode}. Delivery is ${formatPrice(this.zone.fee)}, added at checkout.`;
    } else {
      out.className = 'notice notice--err';
      out.innerHTML = `We don't deliver to ${postcode} yet. Choose pickup, or <a class="link" href="${window.theme?.routes?.customRequest || '/pages/custom-request'}">send us a request</a>.`;
    }
  }

  update() {
    const delivery = this.state.method === 'Delivery';
    $$('[data-delivery-only]', this).forEach((el) => { el.hidden = !delivery; });
    $$('[data-pickup-only]', this).forEach((el) => { el.hidden = this.state.method !== 'Pickup'; });
    const ready = this.missing.length === 0;
    this.toggleAttribute('data-ready', ready);
    this.checkoutButtons().forEach((b) => b.setAttribute('aria-disabled', String(!ready)));
    const message = $('[data-missing]', this);
    if (ready) message.textContent = '';
  }

  async persist() {
    const s = this.state;
    const day = this.days.find((d) => d.iso === s.date);
    const delivery = s.method === 'Delivery';
    this.saving = updateAttributes({
      Fulfilment: s.method,
      Date: s.date,
      Day: day?.longLabel || '',
      Time: s.slot,
      Postcode: delivery ? s.postcode : '',
      'Delivery notes': delivery ? s.notes : '',
      Surprise: delivery && s.surprise ? 'Yes, please don’t call ahead' : '',
    }).then(() => true, () => false);
    return this.saving;
  }

  checkoutButtons() {
    const scope = this.closest('[data-cart-scope]') || document;
    return $$('[data-checkout]', scope);
  }

  guardCheckout() {
    this.checkoutButtons().forEach((button) => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        const needs = this.missing;
        if (needs.length) {
          $('[data-missing]', this).textContent = `Almost there. Choose ${needs.join(', ')}.`;
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        button.classList.add('is-loading');
        if (!(await this.persist())) {
          button.classList.remove('is-loading');
          $('[data-missing]', this).textContent = 'We couldn\u2019t save your day and time. Check your connection and try again.';
          return;
        }
        window.location.href = `${window.theme?.routes?.root || '/'}checkout`.replace('//', '/');
      });
    });
  }
}

customElements.define('delivery-picker', DeliveryPicker);
